import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Blueprint — an architect's drafting table. Floor plans draw themselves with
// crisp lines on a grid, then details (doors, windows, fixtures, furniture,
// room labels) ink in, the plan lingers, fades, and another draws elsewhere.
// A persistent title block, north arrow, and scale bar frame the sheet.

const GRID_SIZE = 40;
const DRAW_SPEED = 200;   // px/sec walls ink in
const LINGER_MS = 4200;
const FADE_MS = 1800;

// A template returns { name, segs, details } in a local 0..w / 0..h box.
// segs: [x1,y1,x2,y2, kind]  kind = 'wall' | 'thin'
// details: drawn after the walls finish — doors/windows/fixtures/labels.
const TEMPLATES = [
  // 2-bedroom apartment
  (w, h) => {
    const segs = [
      [0, 0, w, 0, 'wall'], [w, 0, w, h, 'wall'], [w, h, 0, h, 'wall'], [0, h, 0, 0, 'wall'],
      [0, h * 0.55, w, h * 0.55, 'wall'],
      [w * 0.5, h * 0.55, w * 0.5, h, 'wall'],
      [w * 0.3, 0, w * 0.3, h * 0.25, 'wall'], [w * 0.3, h * 0.25, 0, h * 0.25, 'wall'],
    ];
    const details = [
      { t: 'label', x: w * 0.55, y: h * 0.28, text: 'LIVING' },
      { t: 'label', x: w * 0.08, y: h * 0.14, text: 'BATH' },
      { t: 'label', x: w * 0.22, y: h * 0.78, text: 'BED 1' },
      { t: 'label', x: w * 0.72, y: h * 0.78, text: 'BED 2' },
      { t: 'label', x: w * 0.6, y: h * 0.5, text: 'KITCHEN', size: 9 },
      // Doors (swing arcs)
      { t: 'door', hx: w * 0.3, hy: h * 0.55, r: h * 0.12, a0: -Math.PI / 2, a1: 0 },
      { t: 'door', hx: w * 0.5, hy: h * 0.78, r: w * 0.1, a0: Math.PI, a1: Math.PI * 1.5 },
      // Windows (double line on outer walls)
      { t: 'window', x1: w * 0.6, y1: 0, x2: w * 0.85, y2: 0 },
      { t: 'window', x1: 0, y1: h * 0.35, x2: 0, y2: h * 0.48 },
      // Kitchen counter + stove + sink
      { t: 'rect', x: w * 0.55, y: h * 0.35, w: w * 0.3, h: h * 0.1 },
      { t: 'circle', x: w * 0.62, y: h * 0.4, r: 5 },
      { t: 'circle', x: w * 0.7, y: h * 0.4, r: 4 },
      // Bath fixtures
      { t: 'rect', x: w * 0.04, y: h * 0.16, w: w * 0.1, h: h * 0.07 },  // tub
      { t: 'ellipse', x: w * 0.22, y: h * 0.2, rx: 5, ry: 6 },           // toilet
      // Beds
      { t: 'rect', x: w * 0.1, y: h * 0.68, w: w * 0.25, h: h * 0.18 },
      { t: 'rect', x: w * 0.6, y: h * 0.68, w: w * 0.25, h: h * 0.18 },
    ];
    return { name: 'UNIT A · 2 BED', segs, details };
  },
  // Studio
  (w, h) => {
    const segs = [
      [0, 0, w, 0, 'wall'], [w, 0, w, h, 'wall'], [w, h, 0, h, 'wall'], [0, h, 0, 0, 'wall'],
      [0, h * 0.35, w * 0.35, h * 0.35, 'wall'], [w * 0.35, 0, w * 0.35, h * 0.35, 'wall'],
    ];
    const details = [
      { t: 'label', x: w * 0.55, y: h * 0.55, text: 'STUDIO' },
      { t: 'label', x: w * 0.08, y: h * 0.2, text: 'BATH', size: 9 },
      { t: 'door', hx: w * 0.35, hy: h * 0.2, r: w * 0.12, a0: Math.PI / 2, a1: Math.PI },
      { t: 'window', x1: w * 0.5, y1: 0, x2: w * 0.85, y2: 0 },
      { t: 'window', x1: w, y1: h * 0.4, x2: w, y2: h * 0.7 },
      // Kitchen counter
      { t: 'rect', x: w * 0.4, y: 0, w: w * 0.55, h: h * 0.12 },
      { t: 'circle', x: w * 0.55, y: h * 0.06, r: 4 },
      // Bath
      { t: 'ellipse', x: w * 0.1, y: h * 0.12, rx: 5, ry: 6 },
      { t: 'rect', x: w * 0.18, y: h * 0.06, w: w * 0.14, h: h * 0.12 },
      // Bed + sofa
      { t: 'rect', x: w * 0.15, y: h * 0.55, w: w * 0.35, h: h * 0.3 },
      { t: 'rect', x: w * 0.6, y: h * 0.6, w: w * 0.3, h: h * 0.15 },
    ];
    return { name: 'UNIT B · STUDIO', segs, details };
  },
  // L-shaped 3BR
  (w, h) => {
    const segs = [
      [0, 0, w * 0.7, 0, 'wall'], [w * 0.7, 0, w * 0.7, h * 0.4, 'wall'],
      [w * 0.7, h * 0.4, w, h * 0.4, 'wall'], [w, h * 0.4, w, h, 'wall'],
      [w, h, 0, h, 'wall'], [0, h, 0, 0, 'wall'],
      [0, h * 0.45, w, h * 0.45, 'wall'],
      [w * 0.35, h * 0.45, w * 0.35, h, 'wall'],
      [w * 0.7, h * 0.45, w * 0.7, h, 'wall'],
    ];
    const details = [
      { t: 'label', x: w * 0.3, y: h * 0.22, text: 'GREAT ROOM', size: 9 },
      { t: 'label', x: w * 0.12, y: h * 0.72, text: 'BED 1', size: 9 },
      { t: 'label', x: w * 0.48, y: h * 0.72, text: 'BED 2', size: 9 },
      { t: 'label', x: w * 0.82, y: h * 0.72, text: 'BED 3', size: 9 },
      { t: 'door', hx: w * 0.35, hy: h * 0.45, r: w * 0.08, a0: 0, a1: Math.PI / 2 },
      { t: 'door', hx: w * 0.7, hy: h * 0.45, r: w * 0.08, a0: Math.PI / 2, a1: Math.PI },
      { t: 'window', x1: 0, y1: h * 0.1, x2: 0, y2: h * 0.3 },
      { t: 'window', x1: w * 0.1, y1: 0, x2: w * 0.4, y2: 0 },
      // Kitchen + bath
      { t: 'rect', x: w * 0.42, y: 0, w: w * 0.28, h: h * 0.12 },
      { t: 'ellipse', x: w * 0.08, y: h * 0.1, rx: 5, ry: 6 },
      { t: 'rect', x: w * 0.04, y: h * 0.04, w: w * 0.1, h: h * 0.08 },
      // Beds
      { t: 'rect', x: w * 0.05, y: h * 0.6, w: w * 0.22, h: h * 0.18 },
      { t: 'rect', x: w * 0.4, y: h * 0.6, w: w * 0.22, h: h * 0.18 },
      { t: 'rect', x: w * 0.74, y: h * 0.6, w: w * 0.2, h: h * 0.18 },
    ];
    return { name: 'UNIT C · 3 BED', segs, details };
  },
];

function offset(d, ox, oy) {
  const o = { ...d };
  for (const k of ['x', 'x1', 'x2', 'hx']) if (k in o) o[k] += ox;
  for (const k of ['y', 'y1', 'y2', 'hy']) if (k in o) o[k] += oy;
  return o;
}

function makePlan(w, h) {
  const tpl = TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)];
  const planW = w * (0.34 + Math.random() * 0.16);
  const planH = h * (0.4 + Math.random() * 0.18);
  const ox = Math.random() * (w - planW - 120) + 50;
  const oy = Math.random() * (h - planH - 140) + 60;
  const { name, segs, details } = tpl(planW, planH);

  let total = 0;
  const enriched = segs.map(s => {
    const seg = { x1: s[0] + ox, y1: s[1] + oy, x2: s[2] + ox, y2: s[3] + oy, kind: s[4], start: total };
    seg.len = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
    total += seg.len;
    return seg;
  });

  return {
    name,
    segments: enriched,
    details: details.map(d => offset(d, ox, oy)),
    totalLength: total,
    bbox: { x: ox, y: oy, w: planW, h: planH },
    startTime: null,
    doneTime: null,
    phase: 'drawing',
  };
}

function drawDetails(ctx, details, prefix, alpha) {
  for (const d of details) {
    const weight = d.t === 'label' ? 0.85 : 0.6;
    ctx.strokeStyle = `${prefix}${weight * alpha})`;
    ctx.fillStyle = `${prefix}${weight * alpha})`;
    ctx.lineWidth = 1;
    if (d.t === 'door') {
      ctx.beginPath();
      ctx.arc(d.hx, d.hy, d.r, d.a0, d.a1);
      ctx.stroke();
      const lx = d.hx + Math.cos(d.a0) * d.r;
      const ly = d.hy + Math.sin(d.a0) * d.r;
      ctx.beginPath();
      ctx.moveTo(d.hx, d.hy);
      ctx.lineTo(lx, ly);
      ctx.stroke();
    } else if (d.t === 'window') {
      const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
      const len = Math.hypot(dx, dy) || 1;
      const px = (-dy / len) * 2, py = (dx / len) * 2;
      ctx.beginPath();
      ctx.moveTo(d.x1 + px, d.y1 + py); ctx.lineTo(d.x2 + px, d.y2 + py);
      ctx.moveTo(d.x1 - px, d.y1 - py); ctx.lineTo(d.x2 - px, d.y2 - py);
      ctx.stroke();
    } else if (d.t === 'rect') {
      ctx.strokeRect(d.x, d.y, d.w, d.h);
    } else if (d.t === 'circle') {
      ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2); ctx.stroke();
    } else if (d.t === 'ellipse') {
      ctx.save();
      ctx.translate(d.x, d.y);
      ctx.scale(d.rx, d.ry);
      ctx.beginPath(); ctx.arc(0, 0, 1, 0, Math.PI * 2);
      ctx.restore();
      ctx.stroke();
    } else if (d.t === 'label') {
      ctx.font = `${d.size || 11}px ui-monospace, Menlo, monospace`;
      ctx.fillText(d.text, d.x, d.y);
    }
  }
}

function drawSheetFurniture(ctx, w, h, prefix, name) {
  // Title block (bottom-right)
  const bw = 220, bh = 92, x = w - bw - 24, y = h - bh - 24;
  ctx.strokeStyle = `${prefix}0.7)`;
  ctx.lineWidth = 1.2;
  ctx.strokeRect(x, y, bw, bh);
  ctx.beginPath();
  ctx.moveTo(x, y + 28); ctx.lineTo(x + bw, y + 28);
  ctx.moveTo(x, y + 60); ctx.lineTo(x + bw, y + 60);
  ctx.stroke();
  ctx.fillStyle = `${prefix}0.85)`;
  ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
  ctx.fillText('JPRO ARCHITECTURE', x + 10, y + 18);
  ctx.fillStyle = `${prefix}0.7)`;
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.fillText(name, x + 10, y + 46);
  ctx.fillText('SCALE 1/4" = 1\'-0"', x + 10, y + 78);
  ctx.fillText('A-101', x + bw - 48, y + 78);

  // North arrow (top-right)
  const ncx = w - 56, ncy = 64, nr = 18;
  ctx.strokeStyle = `${prefix}0.6)`;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.arc(ncx, ncy, nr, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = `${prefix}0.8)`;
  ctx.beginPath();
  ctx.moveTo(ncx, ncy - nr + 3);
  ctx.lineTo(ncx - 5, ncy + 6);
  ctx.lineTo(ncx, ncy + 2);
  ctx.lineTo(ncx + 5, ncy + 6);
  ctx.closePath();
  ctx.fill();
  ctx.font = 'bold 11px ui-monospace, Menlo, monospace';
  ctx.fillText('N', ncx - 3, ncy - nr - 5);

  // Scale bar (bottom-left)
  const sx = 28, sy = h - 34, seg = 26, n = 4;
  ctx.strokeStyle = `${prefix}0.6)`;
  ctx.lineWidth = 1;
  ctx.strokeRect(sx, sy - 4, seg * n, 8);
  ctx.fillStyle = `${prefix}0.55)`;
  for (let i = 0; i < n; i += 2) ctx.fillRect(sx + seg * i, sy - 4, seg, 8);
  ctx.fillStyle = `${prefix}0.7)`;
  ctx.font = '9px ui-monospace, Menlo, monospace';
  ctx.fillText('0', sx - 2, sy + 18);
  ctx.fillText("20'", sx + seg * n - 10, sy + 18);
}

export default function BlueprintTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.plans = [makePlan(w, h)];
    },
    draw: (ctx, w, h, state, t) => {
      const bg = dark ? '#0b3a5e' : '#e0efff';
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Minor grid
      ctx.strokeStyle = dark ? 'rgba(255,255,255,0.06)' : 'rgba(30,64,175,0.08)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let x = 0; x <= w; x += GRID_SIZE) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
      for (let y = 0; y <= h; y += GRID_SIZE) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
      ctx.stroke();
      // Major grid
      ctx.strokeStyle = dark ? 'rgba(255,255,255,0.12)' : 'rgba(30,64,175,0.18)';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (let x = 0; x <= w; x += GRID_SIZE * 5) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
      for (let y = 0; y <= h; y += GRID_SIZE * 5) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
      ctx.stroke();

      const idx = Math.floor(t * (colorMode === 'cycling' ? 0.3 : 0.05)) % palette.length;
      const rgb = hexToRgb(palette[idx]);
      const prefix = dark ? 'rgba(255,255,255,' : `rgba(${rgb.r},${rgb.g},${rgb.b},`;

      const now = performance.now();
      let activeName = state.plans[0]?.name || '';

      for (const plan of state.plans) {
        if (plan.startTime === null) plan.startTime = now;

        if (plan.phase === 'drawing') {
          activeName = plan.name;
          const drawn = ((now - plan.startTime) / 1000) * DRAW_SPEED;
          for (const seg of plan.segments) {
            if (seg.start > drawn) break;
            const k = Math.min(1, (drawn - seg.start) / seg.len);
            if (k <= 0) continue;
            ctx.strokeStyle = seg.kind === 'wall' ? `${prefix}0.95)` : `${prefix}0.55)`;
            ctx.lineWidth = seg.kind === 'wall' ? 2.2 : 1;
            ctx.beginPath();
            ctx.moveTo(seg.x1, seg.y1);
            ctx.lineTo(seg.x1 + (seg.x2 - seg.x1) * k, seg.y1 + (seg.y2 - seg.y1) * k);
            ctx.stroke();
          }
          if (drawn >= plan.totalLength) { plan.phase = 'linger'; plan.doneTime = now; }
        } else {
          let alpha = 1;
          if (plan.phase === 'linger' && now - plan.doneTime > LINGER_MS) { plan.phase = 'fading'; plan.doneTime = now; }
          if (plan.phase === 'fading') {
            alpha = Math.max(0, 1 - (now - plan.doneTime) / FADE_MS);
            if (alpha <= 0) plan.phase = 'done';
          }
          // Walls
          for (const seg of plan.segments) {
            ctx.strokeStyle = `${prefix}${(seg.kind === 'wall' ? 0.95 : 0.55) * alpha})`;
            ctx.lineWidth = seg.kind === 'wall' ? 2.2 : 1;
            ctx.beginPath();
            ctx.moveTo(seg.x1, seg.y1);
            ctx.lineTo(seg.x2, seg.y2);
            ctx.stroke();
          }
          // Details + dimension
          drawDetails(ctx, plan.details, prefix, alpha);
          ctx.fillStyle = `${prefix}${0.7 * alpha})`;
          ctx.font = '11px ui-monospace, Menlo, monospace';
          ctx.fillText(`${Math.round(plan.bbox.w / 8)}' × ${Math.round(plan.bbox.h / 8)}'`, plan.bbox.x, plan.bbox.y - 10);
        }
      }

      state.plans = state.plans.filter(p => p.phase !== 'done');
      if (state.plans.length === 0
        || (state.plans.length < 2 && state.plans[state.plans.length - 1].phase !== 'drawing' && Math.random() < 0.01)) {
        state.plans.push(makePlan(w, h));
      }

      drawSheetFurniture(ctx, w, h, prefix, activeName);
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
